import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const LocationHistory = ({ asset, isEditing, onUpdate }) => {
  const [showFloorPlan, setShowFloorPlan] = useState(false);

  const locationOptions = [
    { value: 'building-a-floor-1', label: 'Building A - Floor 1' },
    { value: 'building-a-floor-2', label: 'Building A - Floor 2' },
    { value: 'building-b-floor-1', label: 'Building B - Floor 1' },
    { value: 'building-c-floor-3', label: 'Building C - Floor 3' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'remote', label: 'Remote Location' }
  ];

  const roomOptions = [
    { value: 'conference-room-a', label: 'Conference Room A' },
    { value: 'conference-room-b', label: 'Conference Room B' },
    { value: 'it-office', label: 'IT Office' },
    { value: 'reception', label: 'Reception' },
    { value: 'accounting', label: 'Accounting' },
    { value: 'hr-office', label: 'HR Office' },
    { value: 'storage-room', label: 'Storage Room' },
    { value: 'server-room', label: 'Server Room' }
  ];

  const locationHistory = asset?.locationHistory || [
    {
      id: 1,
      location: 'Building A - Floor 2',
      room: 'IT Office',
      coordinates: { x: 150, y: 200 },
      movedBy: 'John Smith',
      movedDate: '2025-08-15T10:30:00Z',
      reason: 'Initial deployment',
      isCurrent: true
    },
    {
      id: 2,
      location: 'Building A - Floor 1',
      room: 'Storage Room',
      coordinates: { x: 100, y: 150 },
      movedBy: 'Sarah Johnson',
      movedDate: '2025-07-20T14:15:00Z',
      reason: 'Temporary storage during office renovation',
      isCurrent: false
    },
    {
      id: 3,
      location: 'Warehouse',
      room: 'Receiving Area',
      coordinates: { x: 50, y: 100 },
      movedBy: 'Mike Wilson',
      movedDate: '2025-07-01T09:00:00Z',
      reason: 'Asset received from vendor',
      isCurrent: false
    }
  ];

  const currentLocation = locationHistory?.find(loc => loc?.isCurrent) || locationHistory?.[0];

  const handleLocationChange = (field, value) => {
    const updatedLocation = { ...currentLocation, [field]: value };
    const updatedHistory = locationHistory?.map(loc => 
      loc?.isCurrent ? updatedLocation : loc
    );
    onUpdate({ ...asset, locationHistory: updatedHistory });
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Location & History</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFloorPlan(!showFloorPlan)}
          iconName={showFloorPlan ? "EyeOff" : "Eye"}
          iconPosition="left"
        >
          {showFloorPlan ? 'Hide' : 'Show'} Floor Plan
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Location */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-foreground mb-3">Current Location</h4>
          
          <Select
            label="Building/Floor"
            options={locationOptions}
            value={currentLocation?.location || ''}
            onChange={(value) => handleLocationChange('location', value)}
            disabled={!isEditing}
            placeholder="Select building and floor"
          />

          <Select
            label="Room/Area"
            options={roomOptions}
            value={currentLocation?.room || ''}
            onChange={(value) => handleLocationChange('room', value)}
            disabled={!isEditing}
            placeholder="Select room or area"
          />

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="MapPin" size={16} className="text-accent" />
              <span className="text-sm font-medium text-foreground">Location Details</span>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>Building: {currentLocation?.location || 'Not specified'}</p>
              <p>Room: {currentLocation?.room || 'Not specified'}</p>
              <p>Last Updated: {currentLocation?.movedDate ? formatDate(currentLocation?.movedDate) : 'Unknown'}</p>
              <p>Updated By: {currentLocation?.movedBy || 'System'}</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" iconName="Navigation" iconPosition="left">
              Get Directions
            </Button>
            <Button variant="outline" size="sm" iconName="QrCode" iconPosition="left">
              Scan Location
            </Button>
          </div>
        </div>

        {/* Floor Plan Preview */}
        <div className="space-y-4">
          {showFloorPlan && (
            <div className="bg-white border-2 border-dashed border-border rounded-lg p-4">
              <h5 className="text-sm font-medium text-foreground mb-3">Interactive Floor Plan</h5>
              <div className="relative bg-muted rounded-lg h-64 overflow-hidden">
                {/* Simplified Floor Plan SVG */}
                <svg viewBox="0 0 400 300" className="w-full h-full">
                  {/* Floor outline */}
                  <rect x="20" y="20" width="360" height="260" fill="white" stroke="#e2e8f0" strokeWidth="2"/>
                  
                  {/* Rooms */}
                  <rect x="30" y="30" width="80" height="60" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1"/>
                  <text x="70" y="65" textAnchor="middle" className="text-xs fill-slate-600">IT Office</text>
                  
                  <rect x="120" y="30" width="100" height="60" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1"/>
                  <text x="170" y="65" textAnchor="middle" className="text-xs fill-slate-600">Conference A</text>
                  
                  <rect x="230" y="30" width="80" height="60" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1"/>
                  <text x="270" y="65" textAnchor="middle" className="text-xs fill-slate-600">Reception</text>
                  
                  <rect x="320" y="30" width="50" height="60" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1"/>
                  <text x="345" y="65" textAnchor="middle" className="text-xs fill-slate-600">Storage</text>
                  
                  <rect x="30" y="100" width="120" height="80" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1"/>
                  <text x="90" y="145" textAnchor="middle" className="text-xs fill-slate-600">Conference B</text>
                  
                  <rect x="160" y="100" width="100" height="80" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1"/>
                  <text x="210" y="145" textAnchor="middle" className="text-xs fill-slate-600">Accounting</text>
                  
                  <rect x="270" y="100" width="100" height="80" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1"/>
                  <text x="320" y="145" textAnchor="middle" className="text-xs fill-slate-600">HR Office</text>
                  
                  <rect x="30" y="190" width="340" height="80" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1"/>
                  <text x="200" y="235" textAnchor="middle" className="text-xs fill-slate-600">Server Room</text>
                  
                  {/* Asset location marker */}
                  <circle cx="70" cy="60" r="6" fill="#059669" stroke="white" strokeWidth="2"/>
                  <text x="70" y="65" textAnchor="middle" className="text-xs fill-white font-medium">A</text>
                </svg>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Green marker shows current asset location. Click and drag to update position.
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Location History Table */}
      <div className="mt-6">
        <h4 className="text-md font-medium text-foreground mb-4">Movement History</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Date & Time</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Location</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Room</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Moved By</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Reason</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {locationHistory?.map((location) => (
                <tr key={location?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
                  <td className="py-3 px-4 text-foreground">
                    {formatDate(location?.movedDate)}
                  </td>
                  <td className="py-3 px-4 text-foreground">
                    {location?.location}
                  </td>
                  <td className="py-3 px-4 text-foreground">
                    {location?.room}
                  </td>
                  <td className="py-3 px-4 text-foreground">
                    {location?.movedBy}
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">
                    {location?.reason}
                  </td>
                  <td className="py-3 px-4">
                    {location?.isCurrent ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success text-success-foreground">
                        <Icon name="MapPin" size={12} className="mr-1" />
                        Current
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        <Icon name="Clock" size={12} className="mr-1" />
                        Historical
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LocationHistory;